import type { ComponentProps, ElementType, HTMLAttributes, PropsWithChildren, ReactNode } from 'react';
import { clsx } from 'clsx';
import { useIsWriteAllowed } from '../access-context';
import { ButtonLink, type ButtonLinkProps } from '../components/button';
import { Button, type ButtonProps } from '../components/button/button';
import type { DrawerProps } from '../components/dialog/drawer/drawer';
import type { ModalProps } from '../components/dialog/modal/modal';
import { useMergedDialogOpenProps } from '../components/dialog/utils';
import { BaseLink, type BaseLinkProps } from '../components/link/base-link';
import { Tooltip, type TooltipProps } from '../components/tooltip/tooltip';

type RequiredModalProps = Pick<ModalProps, 'trigger' | 'isOpen' | 'onOpenChange'>;

type RequiredDrawerProps = Pick<DrawerProps, 'trigger' | 'isOpen' | 'onOpenChange'>;

export interface WriteGated {
  requiresWrite?: boolean;
  // requiresScope?: string;
}

export interface DrawerInteraction<TComponent extends ElementType = ElementType> extends WriteGated {
  type: 'drawer';
  drawer: {
    Component: TComponent;
    props?: Omit<ComponentProps<TComponent>, keyof RequiredDrawerProps> & Partial<RequiredDrawerProps>;
  };
}

export interface ModalInteraction<TComponent extends ElementType = ElementType> extends WriteGated {
  type: 'modal';
  modal: {
    Component: TComponent;
    props: Omit<ComponentProps<TComponent>, keyof RequiredModalProps> & Partial<RequiredModalProps>;
  };
}

export interface LinkInteraction extends WriteGated {
  type: 'link';
  link: Omit<BaseLinkProps, 'to'> & { to?: string };
}

export interface ButtonInteraction extends WriteGated {
  type: 'button';
  button: HTMLAttributes<HTMLButtonElement>;
}

export interface StyledButtonInteraction extends WriteGated {
  type: 'styledButton';
  styledButton: ButtonProps;
}

export interface StyledButtonLinkInteraction extends WriteGated {
  type: 'styledButtonLink';
  styledButtonLink: ButtonLinkProps;
}

export interface TooltipInteraction extends WriteGated {
  type: 'tooltip';
  tooltip: TooltipProps;
}

export interface DropdownMenuInteraction<TComponent extends ElementType = ElementType> extends WriteGated {
  type: 'dropdownMenu';
  dropdownMenu: {
    Component: TComponent;
    props: ComponentProps<TComponent>;
  };
}

export type Interaction =
  | DrawerInteraction
  | ModalInteraction
  | DropdownMenuInteraction
  | LinkInteraction
  | StyledButtonLinkInteraction
  | ButtonInteraction
  | StyledButtonInteraction
  | TooltipInteraction;

function InteractiveDrawer({ trigger, ...drawer }: DrawerInteraction['drawer'] & { trigger: ReactNode }) {
  const [isOpen, setIsOpen] = useMergedDialogOpenProps({ isOpen: drawer.props?.isOpen, onOpenChange: drawer.props?.onOpenChange });

  return <drawer.Component {...drawer.props} isOpen={isOpen} onOpenChange={setIsOpen} trigger={trigger} />;
}

function InteractiveModal({ trigger, ...modal }: ModalInteraction['modal'] & { trigger: ReactNode }) {
  const [isOpen, setIsOpen] = useMergedDialogOpenProps({ isOpen: modal.props?.isOpen, onOpenChange: modal.props?.onOpenChange });

  return <modal.Component {...modal.props} isOpen={isOpen} onOpenChange={setIsOpen} trigger={trigger} />;
}

function InteractiveDropdownMenu({ activator, ...dropdownMenu }: DropdownMenuInteraction['dropdownMenu'] & { activator: ReactNode }) {
  const [isOpen, setIsOpen] = useMergedDialogOpenProps({ isOpen: dropdownMenu.props?.isOpen, onOpenChange: dropdownMenu.props?.onOpenChange });

  return (
    <dropdownMenu.Component
      align="end"
      labelIsHidden
      matchActivatorWidth={false}
      side="bottom"
      {...dropdownMenu.props}
      activator={activator}
      activatorProps={activator ? null : dropdownMenu.props.activatorProps}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    />
  );
}

function InteractiveTooltip({ activator, ...tooltip }: TooltipInteraction['tooltip'] & { activator: ReactNode }) {
  const [isOpen, setIsOpen] = useMergedDialogOpenProps({ isOpen: tooltip.open, onOpenChange: tooltip.onOpenChange });
  const { children, ...rest } = tooltip;

  return (
    <Tooltip activator={activator} onOpenChange={setIsOpen} open={isOpen} {...rest}>
      {children}
    </Tooltip>
  );
}

export function buildTooltipInteraction(
  content: ReactNode,
  activator: ReactNode,
  props?: Partial<Omit<TooltipProps, 'children' | 'activator'>>,
): TooltipInteraction {
  return {
    type: 'tooltip',
    tooltip: {
      children: content,
      activator,
      ...props,
    },
  };
}

type InteractiveProps = PropsWithChildren<Interaction> & {
  className?: string;
};

export function Interactive({ children, className, ...props }: InteractiveProps) {
  const isWriteAllowed = useIsWriteAllowed();

  if (props.requiresWrite && !isWriteAllowed) {
    return null;
  }

  switch (props.type) {
    case 'drawer':
      return <InteractiveDrawer {...props.drawer} trigger={children ?? props.drawer.props?.trigger} />;
    case 'modal':
      return <InteractiveModal {...props.modal} trigger={children ?? props.modal.props.trigger} />;
    case 'dropdownMenu':
      return <InteractiveDropdownMenu {...props.dropdownMenu} activator={children ?? props.dropdownMenu.props.activator} />;
    case 'link':
      if (!props.link.to) {
        return children ?? props.link.children ?? null;
      }

      return (
        <BaseLink {...props.link} className={clsx(props.link.className, className)} LinkElement={props.link.LinkElement ?? 'a'} to={props.link.to}>
          {children ?? props.link.children}
        </BaseLink>
      );
    case 'styledButtonLink':
      return (
        <ButtonLink {...props.styledButtonLink} className={clsx(props.styledButtonLink.className, className)}>
          {children ?? props.styledButtonLink.children}
        </ButtonLink>
      );
    case 'button':
      return (
        <button {...props.button} className={clsx(props.button.className, className)} type="button">
          {children ?? props.button.children}
        </button>
      );
    case 'styledButton':
      return (
        <Button {...props.styledButton} className={clsx(props.styledButton.className, className)}>
          {children ?? props.styledButton.children}
        </Button>
      );
    case 'tooltip':
      return <InteractiveTooltip {...props.tooltip} activator={children ?? props.tooltip.activator} />;
    default:
      return children;
  }
}
